import {
  Box,
  Button,
  Checkbox,
  ComponentDefaultProps,
  Divider,
  Flex,
  HStack,
  IconButton,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Radio,
  RadioGroup,
  Tab,
  Table,
  TableContainer,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  UnorderedList,
  useToast,
  VStack
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import {
  eachDayOfInterval,
  eachMonthOfInterval,
  endOfMonth,
  format,
  parse,
  startOfMonth,
  subMonths
} from 'date-fns';
import { addMonths } from 'date-fns/esm';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  SVGProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Range } from 'react-date-range';

import { RangeDatePicker } from '../../../../../components/inputs/RangeDatePicker/RangeDatePicker';
import { ExtratoPrevicenciaRepository } from '../../../../../repositories/extrato_previdencia.repository';
import { db } from '../../../../../services/db';
import { ExtratoService } from '../../../../../services/previdencia-individual/extrato_previdencia.service';
import { DatePicker } from './DatePicker';

export type ExtratoModalDTO = {
  ev: string;
  data_vigencia: string;
  cpf: string;
  proposta: string;
};

type ExtratoModalProps = {
  isOpen: boolean;
  onClose: () => void;
  data?: ExtratoModalDTO;
  onSuccessRequest?: ({ cpf }: { cpf?: string }) => void;
};

export const ExtratoModal = ({
  isOpen,
  onClose,
  data,
  onSuccessRequest
}: ExtratoModalProps) => {
  const toast = useToast();
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [extratoModeFilter, setExtratoModeFilter] = useState<
    'period' | 'monthly' | null
  >(null);
  const [dateRange, setDateRange] = useState<Range>();
  const [monthlyDatesSelected, setMonthlyDatesSelected] =
    useState<DateInterval[]>();

  const {
    isLoading,
    mutate: mutateExtrato,
    reset,
    data: urlsData
  } = useMutation({
    mutationKey: ['extrato-previdencia'],
    mutationFn: (body: { data_inicial: string; data_final: string }[]) => {
      const extratoRepository = new ExtratoPrevicenciaRepository(db);
      const extratoService = ExtratoService(extratoRepository, {
        cpf: String(data?.cpf),
        ev: Number(data?.ev),
        proposta: String(data?.proposta)
      });

      return extratoService.getPDFLinks(body);
    },
    onError: (error) => {
      toast({
        status: 'error',
        title: 'Desculpe, um erro ao baixar o PDF. Lamentamos o transtorno.',
        description: 'Tente novamente mais tarde!',
        isClosable: true
      });
      console.log(error);
    },
    onSuccess: (data) => {
      console.log('pdfs ', { data });
    }
  });
  const savedExtracts = useLiveQuery(() => {
    if (!data) return [];

    const items = db.extratoCarteira
      .where('ev')
      .equals(Number(data?.ev))
      .and(
        (extrato) =>
          extrato.cpf === String(data?.cpf) &&
          extrato.proposta === String(data?.proposta)
      )
      .toArray();
    return items;
  }, [data]);

  const isModalButtonFooterDisabled = useMemo(() => {
    if (tabIndex === 0 && !extratoModeFilter) return true;

    if (extratoModeFilter === 'period' && !dateRange) return true;

    if (extratoModeFilter === 'monthly' && monthlyDatesSelected?.length === 0)
      return true;

    if (tabIndex === 1 && monthlyDatesSelected?.length === 0) return true;
    if (tabIndex === 1 && savedExtracts?.length === 0) return true;

    return false;
  }, [tabIndex, extratoModeFilter, dateRange, monthlyDatesSelected]);

  const handleClose = () => {
    setTabIndex(0);
    setExtratoModeFilter(null);
    setDateRange(undefined);
    setMonthlyDatesSelected(undefined);
    setExtratoModeFilter(null);
    reset();
    onClose();
  };

  const handleModalButtonFooterClick = useCallback(async () => {
    let payload = [] as any[];
    if (tabIndex === 0) {
      // Emitir novos PDFs
      if (extratoModeFilter === 'period') {
        payload.push({
          data_inicial: format(
            dateRange?.startDate ?? new Date(),
            'dd/MM/yyyy'
          ),
          data_final: format(dateRange?.endDate ?? new Date(), 'dd/MM/yyyy')
        });
      } else if (extratoModeFilter === 'monthly') {
        payload = monthlyDatesSelected?.map((date) => ({
          data_inicial: format(date.initialDate, 'dd/MM/yyyy'),
          data_final: format(date.finalDate, 'dd/MM/yyyy')
        })) as any[];
      }

      mutateExtrato(payload);
    }

    if (tabIndex === 1) {
      payload = monthlyDatesSelected?.map((date) => ({
        data_inicial: format(date.initialDate, 'dd/MM/yyyy'),
        data_final: format(date.finalDate, 'dd/MM/yyyy')
      })) as any[];
      mutateExtrato(payload);
    }
  }, [tabIndex, extratoModeFilter, dateRange, monthlyDatesSelected]);

  const monthlyIntervalDates = useMemo(() => {
    return eachMonthOfInterval({
      start: new Date(data?.data_vigencia || new Date()),
      end: Date.now()
    })
      .reverse()
      .map((month) => {
        const initialDate = startOfMonth(month);
        const finalDate = endOfMonth(month);

        return {
          initialDate,
          finalDate,
          checked: false,
          status: 'idle'
        } as DateInterval;
      });
  }, [data]);
  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent maxW={750} p={0}>
        <ModalHeader>Emissão de extrato</ModalHeader>
        <ModalCloseButton />
        <ModalBody p={0}>
          <Tabs
            isFitted
            onChange={(index) => setTabIndex(index)}
            tabIndex={tabIndex}
          >
            <TabList bg={'#F4F4F4'} color="#828282">
              <Tab
                _focus={{ outline: '0 !important' }}
                _selected={{
                  color: '#FF5000',
                  borderColor: '#FF5000',
                  fontWeight: 'semibold',
                  bg: '#FFF'
                }}
              >
                <Flex alignItems={'center'} gap={2}>
                  <ExtratoIcon />
                  Emitir novo extrato
                </Flex>
              </Tab>
              <Tab
                _focus={{ outline: '0 !important' }}
                _selected={{
                  color: '#FF5000',
                  borderColor: '#FF5000',
                  fontWeight: 'semibold',
                  bg: '#FFF'
                }}
              >
                <Flex alignItems={'center'} gap={2}>
                  <FolderIcon />
                  Extratos emitidos
                </Flex>
              </Tab>
            </TabList>
            <TabPanels p={3}>
              <TabPanel px={0}>
                <VStack
                  alignItems={'start'}
                  p={3}
                  bg="#F4F4F4"
                  borderRadius={10}
                >
                  <Text as={'b'}>Filtro</Text>
                  <RadioGroup
                    w="full"
                    onChange={(value: string) =>
                      setExtratoModeFilter(value as any)
                    }
                  >
                    <Flex justifyContent={'space-between'}>
                      <Box w="50%">
                        <Radio value="period" colorScheme={'orange'}>
                          Busque por um período específico
                        </Radio>
                      </Box>
                      <Box w="50%">
                        <Radio value="monthly" colorScheme={'orange'}>
                          Filtrar mês a mês
                        </Radio>
                      </Box>
                    </Flex>
                  </RadioGroup>
                </VStack>
                {extratoModeFilter === 'period' ? (
                  <VStack mb={3}>
                    <DatePicker
                      prevLimitDate={
                        new Date(data?.data_vigencia || new Date())
                      }
                      onDateRangeSelected={(range) => setDateRange(range)}
                    />
                  </VStack>
                ) : extratoModeFilter === 'monthly' ? (
                  <VStack alignItems={'start'}>
                    <DatesList
                      dates={monthlyIntervalDates}
                      onDownloadDate={(interval) =>
                        mutateExtrato([
                          {
                            data_inicial: format(
                              interval.initialDate,
                              'dd/MM/yyyy'
                            ),
                            data_final: format(interval.finalDate, 'dd/MM/yyyy')
                          }
                        ])
                      }
                      onCheckDates={(intervals) => {
                        setMonthlyDatesSelected(intervals);
                      }}
                    />
                  </VStack>
                ) : undefined}
              </TabPanel>
              <TabPanel>
                {!savedExtracts || savedExtracts.length === 0 ? (
                  <Text>Não há extratos emitidos</Text>
                ) : (
                  <VStack alignItems={'start'}>
                    <DatesList
                      dates={savedExtracts.map((item) => ({
                        initialDate: parse(
                          item.data_inicial,
                          'dd/MM/yyyy',
                          new Date()
                        ),
                        finalDate: parse(
                          item.data_final,
                          'dd/MM/yyyy',
                          new Date()
                        ),
                        checked: false
                      }))}
                      onDownloadDate={(interval) =>
                        mutateExtrato([
                          {
                            data_inicial: format(
                              interval.initialDate,
                              'dd/MM/yyyy'
                            ),
                            data_final: format(interval.finalDate, 'dd/MM/yyyy')
                          }
                        ])
                      }
                      onCheckDates={(intervals) => {
                        setMonthlyDatesSelected(intervals);
                      }}
                    />
                  </VStack>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        <Divider />
        <ModalFooter justifyContent={'start'}>
          <Button
            bg={'#FF5000'}
            size="md"
            minW={220}
            borderRadius={20}
            color={'#FFF'}
            _hover={{ bg: '#DF4802' }}
            _active={{ bg: '#DF4802' }}
            disabled={isModalButtonFooterDisabled}
            onClick={handleModalButtonFooterClick}
          >
            <Box mr={3}>
              <DownloadIcon />
            </Box>
            {tabIndex == 0
              ? 'Download do extrato completo'
              : 'Reemitir Extratos'}
          </Button>
          <Box>
            {urlsData
              ? urlsData?.map((url, i) => (
                  <iframe
                    key={i}
                    src={url + '#toolbar=0'}
                    width="0px"
                    height="0px"
                  />
                ))
              : undefined}
          </Box>
        </ModalFooter>
        {isLoading && (
          <Progress size="md" isIndeterminate colorScheme={'orange'} />
        )}
      </ModalContent>
    </Modal>
  );
};

type DateInterval = {
  initialDate: Date;
  finalDate: Date;
  checked?: boolean;
};
const DatesList = (props: {
  dates: DateInterval[];
  onDownloadDate?: (interval: DateInterval) => void;
  onCheckDates?: (intervals: DateInterval[]) => void;
}) => {
  const [datesInterval, setDatesInterval] = useState(props.dates);

  useEffect(() => {
    if (props.onCheckDates)
      props.onCheckDates(
        [...datesInterval]
          .filter((e) => e.checked)
          .map((e) => ({
            ...e,
            initialDate: e.initialDate,
            finalDate: e.finalDate
          }))
      );
  }, [datesInterval]);

  const handleCheckboxChecked = (checked: boolean, index: number) => {
    setDatesInterval((state) => {
      const newArr = [...state];
      const item = { ...newArr[index], checked };
      newArr[index] = item;
      return newArr;
    });
  };

  return (
    <VStack w={'100%'} maxH={300} overflowY={'scroll'}>
      <HStack
        w={'100%'}
        px={'28px'}
        pt={'30px'}
        justifyContent={'space-between'}
      >
        <Flex pl={'52px'}>
          <Text as={'b'}>De</Text>
          <Text as={'b'} ml="130">
            Até
          </Text>
        </Flex>
        <Text as={'b'}>Download</Text>
      </HStack>
      {datesInterval.map((interval, i) => {
        const startDayOfMonth = interval.initialDate;
        const endDayOfMonth = interval.finalDate;
        return (
          <HStack
            w={'100%'}
            h={'50px'}
            bg={i % 2 === 0 ? '#F4F4F4' : ' #001E641A'}
            key={String(startDayOfMonth)}
            borderLeft="5px solid #001E64"
            borderRadius={5}
            px={'28px'}
            justifyContent="space-between"
          >
            <Flex w={300} h={50} alignItems={'center'} gap={30}>
              <Checkbox
                borderColor={'#001E64'}
                colorScheme={'orange'}
                _focus={{ outline: 'none' }}
                size={'lg'}
                isChecked={interval.checked}
                onChange={(event) =>
                  handleCheckboxChecked(event.target.checked, i)
                }
              />
              <Text>{format(startDayOfMonth, 'dd/MM/yyyy')}</Text>
              <Divider h={30} w="1px" bg="#D2D2D2" orientation="vertical" />
              <Text>{format(endDayOfMonth, 'dd/MM/yyyy')}</Text>
            </Flex>
            <Box>
              <IconButton
                bg="transparent"
                aria-label="download extrato"
                icon={<DownloadIcon />}
                onClick={() =>
                  props.onDownloadDate
                    ? props.onDownloadDate({
                        initialDate: startDayOfMonth,
                        finalDate: endDayOfMonth
                      })
                    : undefined
                }
              />
            </Box>
          </HStack>
        );
      })}
    </VStack>
  );
};

const FolderIcon = () => (
  <svg
    width="20"
    height="15"
    viewBox="0 0 20 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.9998 14.9951H2.00024C1.46993 14.9945 0.961533 14.7787 0.586552 14.3952C0.211571 14.0117 0.000630188 13.4917 0 12.9493V2.04578C0.000630188 1.50341 0.211571 0.983429 0.586552 0.599909C0.961533 0.216389 1.46993 0.000644538 2.00024 0H5.33397C5.86427 0.000644538 6.37267 0.216389 6.74765 0.599909C7.12263 0.983429 7.33357 1.50341 7.3342 2.04578V2.72771H17.9998C18.5301 2.72836 19.0385 2.94411 19.4134 3.32763C19.7884 3.71115 19.9994 4.23112 20 4.7735V12.9542C19.9987 13.4959 19.7874 14.0151 19.4125 14.3977C19.0375 14.7803 18.5294 14.9951 17.9998 14.9951ZM2.00024 1.35655C1.8236 1.35719 1.65437 1.42924 1.52947 1.55699C1.40456 1.68474 1.33412 1.85782 1.33349 2.03848V12.9469C1.33349 13.0365 1.35074 13.1251 1.38425 13.2079C1.41775 13.2906 1.46686 13.3658 1.52878 13.4291C1.59069 13.4924 1.66419 13.5427 1.74509 13.5769C1.82598 13.6112 1.91268 13.6288 2.00024 13.6288H17.9998C18.1766 13.6288 18.3462 13.557 18.4712 13.4291C18.5963 13.3012 18.6665 13.1278 18.6665 12.9469V4.76863C18.6665 4.58777 18.5963 4.41431 18.4712 4.28643C18.3462 4.15854 18.1766 4.0867 17.9998 4.0867H6.66746C6.49063 4.0867 6.32104 4.01485 6.196 3.88696C6.07096 3.75908 6.00072 3.58563 6.00072 3.40478V2.04578C6.00009 1.86512 5.92964 1.69204 5.80473 1.5643C5.67983 1.43655 5.5106 1.3645 5.33397 1.36386L2.00024 1.35655Z"
      fill="currentcolor"
    />
    <path
      d="M11.3347 4.0867C11.1579 4.0867 10.9883 4.01485 10.8632 3.88696C10.7382 3.75908 10.6679 3.58563 10.6679 3.40478V2.04578C10.6673 1.86512 10.5969 1.69204 10.472 1.5643C10.3471 1.43655 10.1778 1.3645 10.0012 1.36386L5.33397 1.36386C5.15713 1.36386 4.98755 1.29201 4.86251 1.16413C4.73747 1.03624 4.66722 0.862785 4.66722 0.681926C4.66722 0.501067 4.73747 0.327618 4.86251 0.199732C4.98755 0.0718453 5.15713 0 5.33397 0H10.0012C10.5315 0.000644538 11.0399 0.216389 11.4149 0.599909C11.7899 0.983429 12.0008 1.50341 12.0014 2.04578V3.40964C11.9995 3.58945 11.9286 3.76129 11.8038 3.88799C11.679 4.01469 11.5105 4.08607 11.3347 4.0867Z"
      fill="currentcolor"
    />
  </svg>
);

const ExtratoIcon = () => (
  <svg
    width="18"
    height="22"
    viewBox="0 0 18 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0.85 6.82061L0.85 6.82022C0.850024 6.78844 0.852877 6.75656 0.858619 6.72499C0.876442 6.62701 0.921442 6.53573 0.989155 6.46304L0.989837 6.46231L0.989839 6.46231L4.42189 2.82726L4.42258 2.82653C4.46714 2.77996 4.51994 2.74268 4.57816 2.71678L4.57858 2.7166L4.58295 2.71467L4.58394 2.71423L4.58394 2.71424C4.64452 2.68803 4.70971 2.67459 4.77569 2.67492M0.85 6.82061L4.77526 2.82492M0.85 6.82061L0.850006 6.82344V6.82358V20.273C0.850006 20.5029 0.936154 20.725 1.09198 20.89C1.24809 21.0554 1.46171 21.15 1.68642 21.15H12.669C12.8937 21.15 13.1073 21.0554 13.2634 20.89C13.4192 20.725 13.5054 20.5029 13.5054 20.273V11.9868C14.0901 11.8108 14.6595 11.5259 15.1905 11.1215C17.1417 9.63533 17.8799 7.06003 17.1714 4.69937C17.1217 4.53377 17.017 4.41821 16.8862 4.35667C16.7586 4.29667 16.6154 4.29211 16.4888 4.32785C16.3622 4.36357 16.2425 4.44238 16.167 4.56159C16.0893 4.68414 16.0657 4.83822 16.1156 5.00449C16.7725 7.19307 15.9189 9.56405 13.9219 10.613C12.1382 11.55 9.88938 11.0967 8.57415 9.53541C7.25368 7.96789 7.12059 5.61094 8.26453 3.89867C9.5959 1.90582 12.1698 1.39535 14.1892 2.56367L14.2644 2.43383L14.1892 2.56367C14.3389 2.65024 14.4943 2.65926 14.6307 2.60941C14.7629 2.56109 14.867 2.46153 14.932 2.34661C14.9971 2.23164 15.0288 2.09105 15.0032 1.95253C14.9769 1.80988 14.8913 1.67958 14.7417 1.59306C12.3479 0.208179 9.53417 0.866961 7.85887 2.67492M0.85 6.82061L7.92491 2.82492M4.77569 2.67492C4.77585 2.67492 4.77601 2.67492 4.77618 2.67493L4.77526 2.82492M4.77569 2.67492H4.77526V2.82492M4.77569 2.67492H7.85887M4.77526 2.82492H7.92491M7.85887 2.67492C7.84371 2.69128 7.82865 2.70774 7.81368 2.72428L7.92491 2.82492M7.85887 2.67492H7.92491V2.82492M3.22275 15.7326L3.33183 15.8355L3.22275 15.7326C3.13129 15.8295 3.0813 15.9591 3.0813 16.0926C3.0813 16.2261 3.13129 16.3558 3.22275 16.4526L3.33183 16.3497L3.22275 16.4526C3.31449 16.5498 3.44082 16.6062 3.57451 16.6062H10.7818C10.9155 16.6062 11.0418 16.5498 11.1336 16.4526L11.0245 16.3497L11.1336 16.4526C11.225 16.3558 11.275 16.2261 11.275 16.0926C11.275 15.9591 11.225 15.8295 11.1336 15.7326L11.0245 15.8355L11.1336 15.7326C11.0418 15.6354 10.9155 15.5791 10.7818 15.5791H3.57451C3.44082 15.5791 3.31449 15.6354 3.22275 15.7326ZM9.65724 5.27286L9.54406 5.37129L9.65724 5.27285C9.54351 5.14209 9.4003 5.08242 9.2546 5.09055C9.1142 5.09838 8.98854 5.16801 8.89723 5.2626C8.71609 5.45024 8.6333 5.78292 8.86732 6.05199L11.2311 8.76987L11.3443 8.67143L11.2311 8.76987C11.3331 8.88706 11.4848 8.94014 11.6255 8.94109C11.7666 8.94205 11.9207 8.89068 12.0232 8.76731L11.9079 8.67143L12.0232 8.7673L17.604 2.05211C17.8304 1.77965 17.7455 1.4471 17.5651 1.25992C17.4741 1.16542 17.3486 1.09551 17.2082 1.08891C17.0621 1.08205 16.9205 1.14487 16.8097 1.27812L11.6171 7.52627L9.65724 5.27286ZM3.57451 11.5172H7.52137C7.65504 11.5172 7.78137 11.4609 7.87311 11.3637C7.96458 11.2668 8.01457 11.1372 8.01457 11.0037C8.01457 10.8702 7.96458 10.7406 7.87311 10.6437C7.78137 10.5465 7.65504 10.4902 7.52137 10.4902H3.57451C3.44083 10.4902 3.3145 10.5465 3.22276 10.6437C3.1313 10.7406 3.0813 10.8702 3.0813 11.0037C3.0813 11.1372 3.1313 11.2668 3.22276 11.3637C3.3145 11.4609 3.44083 11.5172 3.57451 11.5172ZM3.57451 14.0617H10.7818C10.9155 14.0617 11.0418 14.0054 11.1336 13.9082C11.225 13.8113 11.275 13.6817 11.275 13.5482C11.275 13.4147 11.225 13.285 11.1336 13.1881C11.0418 13.091 10.9155 13.0346 10.7818 13.0346H3.57451C3.44083 13.0346 3.3145 13.091 3.22276 13.1881C3.13129 13.285 3.0813 13.4147 3.0813 13.5482C3.0813 13.6817 3.13129 13.8113 3.22276 13.9082C3.3145 14.0054 3.44083 14.0617 3.57451 14.0617ZM6.4126 7.09609C6.74771 10.3426 9.65149 12.4615 12.519 12.1814V20.123H1.83642V7.33689H4.43143C4.65613 7.33689 4.86975 7.24226 5.02586 7.07693C5.18169 6.91188 5.26784 6.68978 5.26784 6.45987V3.70193H7.10495C6.55597 4.66473 6.28111 5.82216 6.4126 7.09609ZM4.28143 6.30987H2.51831L4.28143 4.44249V6.30987Z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="0.3"
    />
  </svg>
);

const DownloadIcon = () => (
  <svg
    width="13"
    height="17"
    viewBox="0 0 13 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13 5.80357H9.28571V0H3.71429V5.80357H0L6.5 12.5744L13 5.80357ZM5.57142 7.7381V1.93453H7.42856V7.7381H8.51499L6.49999 9.83706L4.48499 7.7381H5.57142ZM13 16.4435V14.5089H0V16.4435H13Z"
      fill="currentColor"
    />
  </svg>
);
